import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

import { WorkflowMonitoringDashboardResponse } from '../models/workflow-monitoring.model';

/**
 * Panel de monitoreo de un workflow: estados de ejecución y sus contadores.
 *
 * <p>Presentacional puro — no llama a la API. Lo comparten la PÁGINA de monitoreo
 * (`/automation/workflows/:id/monitoring`) y la pestaña «Monitoreo» del modal de detalle,
 * para que las etiquetas y las claves del DTO vivan en un solo sitio.</p>
 *
 * <p>Las claves son las del record `WorkflowMonitoringTotals` del backend. Dos trampas ya
 * pagadas: el total es `totalEnrollments` (no `total`), y las tasas llegan en base 100
 * (42.9 = 42,9 %), así que NO se multiplican por 100 al pintarlas.</p>
 */
@Component({
  selector: 'app-workflow-monitoring-summary',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './workflow-monitoring-summary.component.html'
})
export class WorkflowMonitoringSummaryComponent {
  @Input() dashboard: WorkflowMonitoringDashboardResponse | null = null;

  /** Modo modal: tarjetas y tipografía más pequeñas para caber en el detalle. */
  @Input() compact = false;

  /** El detalle no necesita el desglose de pasos ni las duraciones: la página sí. */
  @Input() showDetails = true;

  /** Errores más frecuentes; en el modal se recortan a los 3 primeros. */
  @Input() showErrors = true;
  @Input() maxErrors = 5;

  readonly statusCards: { key: string; label: string; color: string }[] = [
    { key: 'totalEnrollments', label: 'Total',        color: 'bg-gray-50 dark:bg-gray-700/40 text-gray-900 dark:text-white' },
    { key: 'active',           label: 'En ejecución', color: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' },
    { key: 'waiting',          label: 'En espera',    color: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400' },
    { key: 'paused',           label: 'Pausados',     color: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400' },
    { key: 'completed',        label: 'Exitosos',     color: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' },
    { key: 'failed',           label: 'Fallidos',     color: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400' },
    { key: 'exited',           label: 'Salidos',      color: 'bg-gray-50 dark:bg-gray-700/40 text-gray-500 dark:text-gray-400' },
    { key: 'cancelled',        label: 'Cancelados',   color: 'bg-gray-50 dark:bg-gray-700/40 text-gray-500 dark:text-gray-400' }
  ];

  readonly rateRows: { key: string; label: string }[] = [
    { key: 'completionRate', label: 'Éxito' },
    { key: 'errorRate',      label: 'Error' },
    { key: 'exitRate',       label: 'Salida' },
    { key: 'cancelRate',     label: 'Cancelación' }
  ];

  readonly stepRows: { key: string; label: string }[] = [
    { key: 'totalSteps',          label: 'Total' },
    { key: 'successfulSteps',     label: 'Exitosos' },
    { key: 'failedSteps',         label: 'Fallidos' },
    { key: 'skippedSteps',        label: 'Omitidos' },
    { key: 'retryScheduledSteps', label: 'En reintento' }
  ];

  readonly durationRows: { key: string; label: string }[] = [
    { key: 'averageDurationMs', label: 'Promedio' },
    { key: 'medianDurationMs',  label: 'Mediana' }
  ];

  /**
   * Un único fallo produce TRES entradas en `topErrors`: el backend agrupa por `código|mensaje`
   * y registra el mismo texto desde la inscripción (`ENROLLMENT`), desde el run (`RUN`) y desde
   * el paso (con su propio código). Al usuario le da igual el origen — veía la misma frase
   * repetida tres veces —, así que aquí se funden por mensaje sumando los recuentos.
   */
  get topErrors(): { message: string; count: number }[] {
    const porMensaje = new Map<string, number>();
    for (const e of this.dashboard?.topErrors ?? []) {
      const mensaje = e.message ?? '';
      porMensaje.set(mensaje, (porMensaje.get(mensaje) ?? 0) + (e.count ?? 0));
    }
    return [...porMensaje.entries()]
      .map(([message, count]) => ({ message, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, this.maxErrors);
  }

  /** Las tasas ya vienen en base 100 desde el backend (`percentage()` multiplica allí). */
  pct(val: number | null | undefined): string {
    if (val === undefined || val === null) return '—';
    return `${val.toFixed(1)}%`;
  }

  num(val: number | null | undefined): string {
    if (val === undefined || val === null) return '—';
    return val.toLocaleString('es');
  }

  formatDuration(ms: number | null | undefined): string {
    if (ms === null || ms === undefined) return '—';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
    return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
  }
}
